import {
  Box,
  Button,
  chakra,
  Checkbox,
  Divider,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { t } from "i18next";
import {
  EsgLabel,
  FormCheckBox,
  FormInput,
  FormMultiSelect,
  MoneyFormInput,
  NumericFormInputGroup,
} from "../../../../../../components/Forms";
import { useForm } from "react-hook-form";
import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import {
  supplierQuestionnaireKeys,
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire,
  useUpdateFinancialYearEndMonth
} from "../../../../../../hooks/query";
import {
  CALCULATION_BOUNDARY_OPTIONS,
  CURRENCY_SYMBOLS,
  greenhouseGases,
  showErrorToast,
  showSuccessToast
} from "../../../../../../utils";
import { useCreateGeneralData, useUpdateGeneralData } from "../../../../../../hooks/query/supplier-questionnaire/tabs";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import {
  Answer
} from "../../../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";
import { ConfirmationDialog } from "../../../../../../components/ConfirmationDialog";
import { useQueryClient } from "react-query";
import { QuestionIconTooltip } from "../../../../../../components";
import { FinancialYearEndMonthSelector } from "../../../FinancialYearEndMonthSelector";
import { LABEL_TYPES, OTHER_OPTION } from "../../../FinancialYearEndMonthSelector/FinancialYearEndMonthSelector";

function mapApiDataToFormData(apiData, financialYearEndMonthObj) {
  const formData = {};
  Object.keys(greenhouseGases).forEach(key => {
    formData[`including${key}Emissions`] = apiData?.greenhouseGases?.[`including${key}Emissions`] ?? false;
  });
  formData["greenhouseGasesAccountingStandards"] = apiData?.greenhouseGasesAccountingStandards?.length > 0 ?
    apiData.greenhouseGasesAccountingStandards.map(el => {
      return {
        label: t(`supplierQuestionnaire.general.accountingStandardsOptions.${el}`, { defaultValue: "" }),
        value: el
      };
    }) : [];
  formData["emissionFactorDatasets"] = apiData?.emissionFactorDatasets ?? "";
  formData["emissionVerifier"] = apiData?.emissionVerifier ?? "";
  formData["howMuchAndHowCarbonAccounted"] = apiData?.howMuchAndHowCarbonAccounted ?? "";
  formData["turnover"] = apiData?.turnover;
  formData["sourcingCompanyPercentage"] = apiData?.sourcingCompanyPercentage ?? 0;
  formData["userAwarenessConfirmation"] = apiData?.userAwarenessConfirmation ?? false;
  formData["financialYearEndMonth"] = financialYearEndMonthObj.month;
  formData["otherClarification"] = financialYearEndMonthObj.otherClarification;

  return formData;
}

function mapFormDataToApiData(formData) {
  return {
    greenhouseGases: {
      includingCarbonDioxideEmissions: formData.includingCarbonDioxideEmissions,
      includingMethaneEmissions: formData.includingMethaneEmissions,
      includingNitrousOxideEmissions: formData.includingNitrousOxideEmissions,
      includingHydrofluorocarbonsEmissions: formData.includingHydrofluorocarbonsEmissions,
      includingPerfluorocarbonsEmissions: formData.includingPerfluorocarbonsEmissions,
      includingSulphurHexafluorideEmissions: formData.includingSulphurHexafluorideEmissions,
      includingCarbonDioxideEquivalentEmissions: formData.includingCarbonDioxideEquivalentEmissions,
      includingNitrogenTrifluorideEmissions: formData.includingNitrogenTrifluorideEmissions
    },
    greenhouseGasesAccountingStandards: formData.greenhouseGasesAccountingStandards.map(el => el.value),
    emissionFactorDatasets: formData.emissionFactorDatasets,
    emissionVerifier: formData.emissionVerifier,
    howMuchAndHowCarbonAccounted: formData.howMuchAndHowCarbonAccounted,
    turnover: formData.turnover,
    sourcingCompanyPercentage: formData.sourcingCompanyPercentage,
    userAwarenessConfirmation: formData.userAwarenessConfirmation
  };
}

const ACCOUNTING_STANDARDS_OPTIONS = Object.freeze({
  "GHG_PROTOCOL": "GHG_PROTOCOL",
  "ISO_14064": "ISO_14064",
  "PCAF": "PCAF"
});

function General({
                   isTabActive, defaultValues, handleTabChange, isPreview,
                   returnToListRequested, declineReturnToListRequest, returnToList
                 }) {
  const { id: questionnaireId, general, financialYearEndMonth } = defaultValues;
  const formDefaultValues = mapApiDataToFormData(general, financialYearEndMonth);
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();

  const {
    register,
    handleSubmit,
    formState,
    control,
    setValue,
    reset,
    watch
  } = useForm({
    defaultValues: formDefaultValues
  });

  const formValues = watch();

  const [isInitialState, setIsInitialState] = useState(true);
  const [finalizeQuestionnaire, setFinalizeQuestionnaire] = useState(false);
  const [shouldChangeTab, setShouldChangeTab] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [emissionsVerified, setEmissionsVerified] = useState();
  const [carbonOffsets, setCarbonOffsets] = useState();
  const [calculationBoundary, setCalculationBoundary] = useState();

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const accountingStandardsContainerWidth = useBreakpointValue({
    lg: "50%",
    base: "100%"
  });
  const calculationBoundaryContainerFlow = useBreakpointValue({
    lg: "row",
    base: "column"
  });

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      if (finalizeQuestionnaire === true) {
        setFinalizeQuestionnaire(false);
      }
      if (returnToListRequested === true) {
        declineReturnToListRequest();
      }
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, finalizeQuestionnaire, returnToListRequested, declineReturnToListRequest, isDirty]);

  useEffect(() => {
    if (isTabActive === true && returnToListRequested === true) {
      const generalForm = document.getElementById("supplier-questionnaire-general");
      if (generalForm) {
        generalForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    }
  }, [isTabActive, returnToListRequested]);

  useEffect(() => {
    if (isInitialState && defaultValues) {
      setEmissionsVerified(defaultValues.general?.emissionVerifiedIndependently === true ? "yes" : "no");
      setCarbonOffsets(defaultValues.general?.companyPurchasedCarbonOffsetInLastTwelveMonths === true ? "yes" : "no");
      setCalculationBoundary(defaultValues.general?.calculationBoundary);
      setIsInitialState(false);
    }
  }, [isInitialState, defaultValues]);

  const queryConfig = {
    onSuccess: () => {
      if (finalizeQuestionnaire) {
        finalize(questionnaireId);
      } else {
        showSuccessToast(toast, t("entitySaved", { entityName: t("supplierQuestionnaire.general.entityName") }));
        queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
        if (returnToListRequested === true) {
          returnToList();
        }
        if (!isLoadingOnCreate && !isLoadingOnUpdate) {
          shouldChangeTab && changeTab();
        }
      }
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: updateFinancialYearEndMonth } = useUpdateFinancialYearEndMonth({
    onError: err => showErrorToast(toast, err.message)
  });
  const { mutate: create, isLoading: isLoadingOnCreate } = useCreateGeneralData(queryConfig);
  const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateGeneralData(queryConfig);
  const { mutate: finalize } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("supplierQuestionnaire.page.finalized"));
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalizeQuestionnaire(false);
    }
  });

  useEffect(() => {
    if (isLoadingOnCreate || isLoadingOnUpdate) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isLoadingOnCreate, isLoadingOnUpdate]);

  const renderIncludedGHGAnswer = () => {
    let includedGasses = [];
    Object.keys(greenhouseGases).forEach(key => {
      if (defaultValues.general?.greenhouseGases?.[`including${key}Emissions`] === true) {
        includedGasses.push({ key: key, label: greenhouseGases[key].label ?? "" });
      }
    });

    const gassesLabels = <Text fontSize="sm" whiteSpace="break-spaces">
      {includedGasses.map((element, index) => {
        return <Fragment key={element.key}>
          {element.label}
          {index < includedGasses.length - 1 ? `, ` : ""}
        </Fragment>;
      })}
    </Text>;

    return <Answer answerText={includedGasses.length > 0 ? gassesLabels : ""} />;
  };

  const renderIncludedGHGSection = () => {
    const allCheckboxes = Object.keys(greenhouseGases).map(key => {
      return <FormCheckBox
        key={key}
        label={greenhouseGases[key].label ?? ""}
        errors={formState.errors}
        {...register(`including${key}Emissions`)}
        control={control}
        display={"flex"}
        fontSize="sm"
      />;
    });
    const checkboxPairs = [];
    allCheckboxes.forEach((item, index) => {
      if (index % 2 === 0) {
        checkboxPairs.push(<Flex key={`checkbox-row-${index}`}>
          {item}
          {index < allCheckboxes.length - 1 ? allCheckboxes[index + 1] : null}
        </Flex>);
      }
    });
    return <Box>{checkboxPairs}</Box>;
  };

  const renderCalculationBoundaryOptions = () => {
    return Object.keys(CALCULATION_BOUNDARY_OPTIONS).map(key =>
      <Flex alignItems="center" key={CALCULATION_BOUNDARY_OPTIONS[key]}>
        <Radio
          colorScheme="customScheme"
          value={CALCULATION_BOUNDARY_OPTIONS[key]}
          onChange={e => {
            setCalculationBoundary(e?.target.value);
          }}
        >
          {t(`supplierQuestionnaire.general.calculationBoundaries.${CALCULATION_BOUNDARY_OPTIONS[key]}`, { defaultValue: "" })}
        </Radio>
        <QuestionIconTooltip
          label={
            t(`supplierQuestionnaire.general.calculationBoundariesTooltips.${CALCULATION_BOUNDARY_OPTIONS[key]}`,
              { defaultValue: "" })
          }
          ariaLabel={`${CALCULATION_BOUNDARY_OPTIONS[key]}-tooltip`}
        />
      </Flex>
    );
  };

  function changeTab() {
    handleTabChange(prev => prev + 1);
    setShouldChangeTab(false);
  }

  const onSubmit = (data) => {
    const apiData = mapFormDataToApiData(data);
    apiData.emissionVerifiedIndependently = emissionsVerified === "yes";
    apiData.companyPurchasedCarbonOffsetInLastTwelveMonths = carbonOffsets === "yes";
    if (calculationBoundary && typeof calculationBoundary !== "undefined") {
      apiData.calculationBoundary = calculationBoundary;
    }
    if (isDirty || apiData.emissionVerifiedIndependently !== general?.emissionVerifiedIndependently
      || apiData.companyPurchasedCarbonOffsetInLastTwelveMonths !== general?.companyPurchasedCarbonOffsetInLastTwelveMonths
      || apiData.calculationBoundary !== general?.calculationBoundary) {
      if (data.financialYearEndMonth !== financialYearEndMonth.month || (
        data.financialYearEndMonth === OTHER_OPTION && data.otherClarification !== financialYearEndMonth.otherClarification
      )) {
        updateFinancialYearEndMonth({
          questionnaireId: questionnaireId,
          body: {
            month: data.financialYearEndMonth,
            otherClarification: data.otherClarification
          }
        });
      }
      if (general?.id) {
        update({ questionnaireId, id: general.id, data: apiData });
      } else {
        create({ questionnaireId, data: apiData });
      }
      reset(data);
    } else {
      return finalizeQuestionnaire ? finalize(questionnaireId) :
        returnToListRequested === true ? returnToList() :
          shouldChangeTab ? changeTab() : null;
    }
  };

  const handleFinalizeConfirmation = () => {
    handleSubmit(onSubmit)();
    handleDialogClose(true);
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalizeQuestionnaire(false);
    }
    onConfirmDialogClose();
  };

  const areAllGassesSelected = () => {
    const foundNotSelectedGas = Object.keys(greenhouseGases)
      .map(key => formValues?.[`including${key}Emissions`] === true)
      .find(el => el === false);

    return typeof foundNotSelectedGas === "undefined";
  };

  const updateAllGasses = (value) => {
    Object.keys(greenhouseGases).forEach(key => {
      setValue(`including${key}Emissions`, value, { shouldDirty: true });
    });
  };

  return (
    <chakra.form id="supplier-questionnaire-general" onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gap={10}>

        <Box>
          <EsgLabel text={t("supplierQuestionnaire.general.includedGases")} />
          {isPreview ?
            renderIncludedGHGAnswer() :
            <>
              {renderIncludedGHGSection()}
              <Checkbox
                isChecked={areAllGassesSelected()}
                onChange={(e) => {
                  updateAllGasses(e.target.checked);
                }}
                width="max-content" maxWidth="100%"
                colorScheme={"customScheme"}
                marginBlockStart={4}
              >
                {t("supplierQuestionnaire.general.allOfTheAbove")}
              </Checkbox>
            </>
          }
        </Box>

        <Box>
          <Flex alignItems="center">
            <EsgLabel text={t("supplierQuestionnaire.general.accountingStandards")} />
            <QuestionIconTooltip
              label={t("supplierQuestionnaire.general.accountingStandardsTooltip")}
              ariaLabel={"accounting-standards-tooltip"}
            />
          </Flex>
          {isPreview ?
            <Answer
              answerText={
                formDefaultValues.greenhouseGasesAccountingStandards
                  .map(el => t(`supplierQuestionnaire.general.accountingStandardsOptions.${el.value}`, { defaultValue: el.value }))
                  .join(", ")
              }
            /> :
            <FormMultiSelect
              control={control}
              name="greenhouseGasesAccountingStandards"
              id="greenhouseGasesAccountingStandards"
              options={Object.keys(ACCOUNTING_STANDARDS_OPTIONS).map(option => {
                return {
                  label: t(`supplierQuestionnaire.general.accountingStandardsOptions.${option}`, { defaultValue: "" }),
                  value: option
                };
              })}
              placeholder={t("supplierQuestionnaire.general.accountingStandardsPlaceholder")}
              errors={formState.errors}
              containerStyles={{ maxWidth: accountingStandardsContainerWidth }}
            />
          }
        </Box>

        <Box>
          <EsgLabel text={t("supplierQuestionnaire.general.calculationBoundary")} />
          {isPreview ?
            <Answer
              answerText={
                t(`supplierQuestionnaire.general.calculationBoundaries.${calculationBoundary}`,
                  { defaultValue: calculationBoundary ?? "" })
              }
            /> :
            <RadioGroup value={calculationBoundary} style={{ position: "relative" }}>
              <Stack
                spacing={calculationBoundaryContainerFlow === "row" ? 7 : 2}
                direction={calculationBoundaryContainerFlow}
              >
                {renderCalculationBoundaryOptions()}
              </Stack>
            </RadioGroup>
          }
        </Box>

        <Box>
          <EsgLabel text={t("supplierQuestionnaire.general.emissionFactorDatasets")} />
          {isPreview ?
            <Answer answerText={formDefaultValues.emissionFactorDatasets} /> :
            <FormInput
              type="text"
              errors={formState.errors}
              {...register("emissionFactorDatasets")}
            />
          }
        </Box>

        <Divider />

        <Box>
          <EsgLabel text={t("supplierQuestionnaire.general.emissionsVerified")} />
          {isPreview ?
            <Answer
              answerText={emissionsVerified ? t(`supplierQuestionnaire.general.${emissionsVerified}`) : ""}
            /> :
            <RadioGroup value={emissionsVerified} mb={5} style={{ position: "relative" }}>
              <Stack spacing={5} direction="row">
                <Radio
                  colorScheme="customScheme"
                  value="yes"
                  onChange={e => {
                    setEmissionsVerified(e?.target.value);
                  }}
                >
                  {t("supplierQuestionnaire.general.yes")}
                </Radio>
                <Radio
                  colorScheme="customScheme"
                  value="no"
                  onChange={e => {
                    setValue("emissionVerifier", "");
                    setEmissionsVerified(e?.target.value);
                  }}
                >
                  {t("supplierQuestionnaire.general.no")}
                </Radio>
              </Stack>
            </RadioGroup>
          }
          <EsgLabel text={t("supplierQuestionnaire.general.verifiedConfirmed")} />
          {isPreview ?
            <Answer answerText={formDefaultValues.emissionVerifier} /> :
            <FormInput
              errors={formState.errors}
              {...register("emissionVerifier", {
                required: emissionsVerified === "yes" ?
                  t("supplierQuestionnaire.general.requiredFiled") : false
              })}
              isDisabled={emissionsVerified === "no"}
            />
          }
        </Box>

        <Divider />

        <Box>
          <EsgLabel text={t("supplierQuestionnaire.general.carbonOffsets")} />
          {isPreview ?
            <Answer
              answerText={carbonOffsets ? t(`supplierQuestionnaire.general.${carbonOffsets}`) : ""}
            /> :
            <RadioGroup value={carbonOffsets} mb={5} style={{ position: "relative" }}>
              <Stack spacing={5} direction="row">
                <Radio
                  colorScheme="customScheme"
                  value="yes"
                  onChange={e => {
                    setCarbonOffsets(e?.target.value);
                  }}
                >
                  {t("supplierQuestionnaire.general.yes")}
                </Radio>
                <Radio
                  colorScheme="customScheme"
                  value="no"
                  onChange={e => {
                    setValue("howMuchAndHowCarbonAccounted", "");
                    setCarbonOffsets(e?.target.value);
                  }}
                >
                  {t("supplierQuestionnaire.general.no")}
                </Radio>
              </Stack>
            </RadioGroup>
          }
          <EsgLabel text={t("supplierQuestionnaire.general.carbonOffsetsConfirmed")} />
          {isPreview ?
            <Answer answerText={formDefaultValues.howMuchAndHowCarbonAccounted} /> :
            <FormInput
              errors={formState.errors}
              {...register("howMuchAndHowCarbonAccounted", {
                required: carbonOffsets === "yes" ?
                  t("supplierQuestionnaire.general.requiredFiled") : false
              })}
              isDisabled={carbonOffsets === "no"}
            />
          }
        </Box>

        <Divider />

        <Box>
          <Flex alignItems="center">
            <EsgLabel text={`${t("supplierQuestionnaire.general.turnover")} *`} />
            <QuestionIconTooltip
              label={t("supplierQuestionnaire.general.turnoverTooltip")}
              ariaLabel={"turnover-tooltip"}
            />
          </Flex>
          {isPreview ?
            <Answer
              answerText={formDefaultValues.turnover ?? ""}
              prefix={formDefaultValues.turnover !== null && typeof formDefaultValues.turnover !== "undefined" ?
                CURRENCY_SYMBOLS.GBP : null}
            /> :
            <MoneyFormInput
              sign={CURRENCY_SYMBOLS.GBP}
              errors={formState.errors}
              {...register("turnover", {
                required: t("supplierQuestionnaire.general.requiredFiled")
              })}
            />
          }
        </Box>

        <Box>
          <EsgLabel text={`${t("supplierQuestionnaire.general.sourcingCompanyPercentage")} *`} />
          {isPreview ?
            <Answer
              answerText={formDefaultValues.sourcingCompanyPercentage !== null ?
                `${formDefaultValues.sourcingCompanyPercentage}%` : ""}
            /> :
            <NumericFormInputGroup
              {...register("sourcingCompanyPercentage", {
                required: t("supplierQuestionnaire.general.sourcingCompanyPercentageRequired"),
                min: {
                  value: 0.001,
                  message: "Min value is 0.001%"
                },
                max: {
                  value: 100,
                  message: "Max value is 100%"
                }
              })}
              errors={formState.errors}
              type="number"
              addonText="%"
            />
          }
        </Box>

        <Box>
          {isPreview ?
            <>
              <EsgLabel text={`${t("supplierQuestionnaire.page.financialYearEnd")} *`} />
              <Answer
                answerText={`${formDefaultValues.financialYearEndMonth !== OTHER_OPTION ?
                  formDefaultValues.financialYearEndMonth ?? "" : formDefaultValues.otherClarification ?? ""}`} />
            </> :
            <FinancialYearEndMonthSelector
              labelType={LABEL_TYPES.ESG_LABEL}
              formState={formState}
              register={register}
              watch={watch}
              setValue={setValue}
            />
          }
        </Box>

        <Box>
          <EsgLabel text={`${t("supplierQuestionnaire.general.autoCalculationReminder")} *`} />
          {isPreview ?
            <Answer answerText={defaultValues.general?.userAwarenessConfirmation === true ?
              t("supplierQuestionnaire.general.userAwarenessConfirmation") : ""}
            /> :
            <>
              <FormCheckBox
                label={t("supplierQuestionnaire.general.userAwarenessConfirmation")}
                errors={formState.errors}
                {...register(`userAwarenessConfirmation`, {
                  required: t("supplierQuestionnaire.general.requiredFiled")
                })}
                control={control}
                display={"flex"}
                fontSize="sm"
              />
            </>
          }
        </Box>

      </Flex>

      {isPreview !== true ? <Flex
        justifyContent="space-between"
        marginBlockStart="10"
        position="sticky"
        bottom="0"
        backgroundColor="#fff"
        borderColor="esg_gray.100"
        bordersyle="solid"
        borderTopWidth="1px"
        paddingBlock="4"
        zIndex="10"
      >
        <Box>
          <Button
            colorScheme="white"
            color="esg_lightgreen"
            border={"1px solid"}
            borderColor={"esg_lightgreen"}
            type="submit"
            width="32"
            onClick={() => setShouldChangeTab(true)}
          >
            {t("buttons.next")}
          </Button>
        </Box>
        <Flex gap="2">
          <Button
            colorScheme="white"
            color="esg_lightgreen"
            border={"1px solid"}
            borderColor={"esg_lightgreen"}
            type="submit"
            width="32"
            onClick={() => setShouldChangeTab(false)}
          >
            {t("buttons.save")}
          </Button>
          <Button
            colorScheme="customScheme"
            width="32"
            onClick={() => {
              setFinalizeQuestionnaire(true);
              onConfirmDialogOpen();
            }}
          >
            {t("buttons.finalize")}
          </Button>
        </Flex>
      </Flex> : null}
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
    </chakra.form>
  );
}

export default General;
